import { orderLayoutBoxes } from '../utils/privateLayouts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../hooks/useNotification';
import { useSyncLanguageUrl } from '../hooks/useSyncLanguageUrl';
import {
  waitForCanvasFonts,
} from '../utils/presetLayout';
import { BUILT_IN_NUMBER_ASSETS } from './VectorLayoutEditor';
import RenameTextBoxModal from '../features/edit-page/components/RenameTextBoxModal';
import AdditionalContentModal from '../features/edit-page/components/AdditionalContentModal';
import EditPageHeader from '../features/edit-page/components/EditPageHeader';
import RenderedPagePanel from '../features/edit-page/components/RenderedPagePanel';
import PageCanvasPanel from '../features/edit-page/components/PageCanvasPanel';
import PageTextEditor from '../features/edit-page/components/PageTextEditor';
import SelectionInspector from '../features/edit-page/components/SelectionInspector';
import WorkflowSidebar from '../features/edit-page/components/WorkflowSidebar';
import ZoomToolbar from '../features/edit-page/components/ZoomToolbar';
import {
  describeLayoutItem,
  logLayoutDebug,
} from '../features/edit-page/model/presetLayoutHelpers';
import { isTextItemVisibleOnCanvas } from '../features/edit-page/model/textItems';
import useCanvasArtwork from '../features/edit-page/hooks/useCanvasArtwork';
import { useVectorLayoutController } from '../features/edit-page/hooks/useVectorLayoutController';
import { usePageLayoutTemplates } from '../features/edit-page/hooks/usePageLayoutTemplates';
import { useEditorViewport } from '../features/edit-page/hooks/useEditorViewport';
import { usePageTextStyles } from '../features/edit-page/hooks/usePageTextStyles';
import usePageTextItems from '../features/edit-page/hooks/usePageTextItems';
import useMoveHistory from '../features/edit-page/hooks/useMoveHistory';
import usePageTranslations from '../features/edit-page/hooks/usePageTranslations';
import { useCanvasOutputController } from '../features/edit-page/hooks/useCanvasOutputController';
import {
  getBook,
  getNextPage,
  getPage,
  getPageTextEntries,
  getPageTextItems,
  getPreviousPage,
  saveRichText,
} from '../features/edit-page/api/pageApi';

const getPairLanguages = (pageLanguage) => (pageLanguage || '').split('_').filter(Boolean);
// Additional-text geometry belongs to the page and is shared by every pair.
const MAIN_TEXT_ROLE = 'main';
const ADDITIONAL_TEXT_ROLE = 'additional';

function EditPagePresets() {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const trRef = useRef(null);
  const [pageLoadComplete, setPageLoadComplete] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 800 });
  const {
    canvasViewportRef,
    effectiveStageScale,
    handleResetZoom,
    handleZoomIn,
    handleZoomOut,
    setShowGrid,
    showGrid,
    stageScale,
  } = useEditorViewport(canvasSize);
  const [pageLanguage, setPageLanguage] = useState(null);
  const [activeWorkflowTab, setActiveWorkflowTab] = useState('page-layout');
  const [renamingEntryId, setRenamingEntryId] = useState(null);
  const [renderingPreview, setRenderingPreview] = useState(false);
  const [savingPage, setSavingPage] = useState(false);
  const [addingMainText, setAddingMainText] = useState(false);
  const [removingRenderedPage, setRemovingRenderedPage] = useState(false);
  const { notify, Notification } = useNotification();

  const [finishedPreviewPath, setFinishedPreviewPath] = useState('');
  const [editorReady, setEditorReady] = useState(false);
  const [editorRenderKey, setEditorRenderKey] = useState(0);
  const textItemsRefForControllers = useRef([]);
  const {
    designPresets,
    bookDefaultPresetId,
    mainTextDisplayMode,
    setBookDefaultPresetId,
  } = usePageTextStyles();

  const {
    getCurrentTextValues,
    saveTextChanges,
    savingFields,
    setTextByLang,
    textByLang,
    translateText,
  } = usePageTranslations({ pageId: id, pageData, notify });
  const {
    addWhiteCover,
    handleImageUpload,
    handleRichTextImage,
    handleSelect,
    image,
    imageUploadRef,
    loadImageSafely,
    resetArtwork,
    selectedId,
    setSelectedId,
    setTextNodes,
    textNodes,
  } = useCanvasArtwork({
    canvasSize,
    layerRef,
    notify,
    onArtworkSelect: () => {
      setSelectedTextItemId(null);
      setActiveWorkflowTab('page-layout');
    },
    trRef,
  });

  const currentLanguages = useMemo(() => getPairLanguages(pageLanguage), [pageLanguage]);
  const {
    additionalContentDraft,
    addMainTextToPage: addMainTextToPageFromHook,
    addPageTextEntry,
    deletePageTextEntry,
    insertAdditionalTextBlock,
    normalizePageTextEntries,
    normalizeSavedTextItems,
    openAdditionalContentModal,
    pageTextEntries,
    renamePageTextEntry,
    savePageTextEntries,
    saveTextItems,
    saveTextItem,
    savingTextEntries,
    savingTextItems,
    selectedTextItemId,
    setAdditionalContentDraft,
    setPageTextEntries,
    setSelectedAdditionalEntryId,
    setSelectedTextItemId,
    setTextItems,
    setShowAdditionalContentModal,
    showAdditionalContentModal,
    textItems,
    textItemsRef,
    updateAdditionalEntryText,
    updatePageTextItemPosition,
  } = usePageTextItems({

    canvasSize,
    bookDefaultPresetId,
    currentLanguages,
    designPresets,
    getCurrentTextValues,
    mainTextDisplayMode,
    notify,
    pageId: id,
    pageLanguage,

    setPageData,

    setSelectedArtworkId: () => setSelectedId(null),
    textItemsRef: textItemsRefForControllers,
  });

  const { canUndoMove, clearMoveHistory, recordMove, undoMove } = useMoveHistory({
    textItems,
    textItemsRef,
    setTextItems,
    textNodes,
    setTextNodes,
  });

  const pageLayoutTemplates = usePageLayoutTemplates({
    activeWorkflowTab,
    bookId: pageData?.book_id,
    pageId: id,
    canvasSize,
    setPageData,
    setTextItems,
    setPageTextEntries,
    pageLanguage,
    pageTextEntries,
    textItemsRef,

    getCurrentTextValues,
    savePageTextEntries,
    saveTextItems,
    setSelectedTextItemId,
    setSelectedAdditionalEntryId,
    notify,
  });

  const {
    applyVectorLayout,
    handleVectorLayoutSelection,
    hiddenDecorationIds,
    selectedVectorLayout,
    selectedVectorLayoutId,
    setSelectedVectorLayoutId,
    vectorLayouts,
  } = useVectorLayoutController({ id, notify, pageData, setPageData });
  const selectedVectorAssets = useMemo(() => {
    if (!selectedVectorLayout) return BUILT_IN_NUMBER_ASSETS;
    return [
      ...BUILT_IN_NUMBER_ASSETS,
      ...(Array.isArray(selectedVectorLayout.assets) ? selectedVectorLayout.assets : []),
    ];
  }, [selectedVectorLayout]);
  // Konva measures text with the browser's currently available font metrics.
  // Mounting it before the preset fonts are available makes the first editor
  // frame vulnerable to fallback-font wrapping.
  useEffect(() => {
    if (!pageLoadComplete || !canvasSize.width || !canvasSize.height) {
      setEditorReady(false);
      return undefined;
    }

    let cancelled = false;
    setEditorReady(false);

    const prepareEditor = async () => {
      await waitForCanvasFonts();
      // Let the browser commit the loaded font faces before Konva measures.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (cancelled) return;
      setEditorRenderKey((current) => current + 1);
      setEditorReady(true);
    };

    prepareEditor().catch((error) => {
      console.error('Failed to prepare canvas fonts', error);
      if (!cancelled) {
        setEditorRenderKey((current) => current + 1);
        setEditorReady(true);
      }
    });

    return () => { cancelled = true; };
  }, [
    canvasSize.height,
    canvasSize.width,
    pageLanguage,
    pageLoadComplete,

  ]);

  const addMainTextToPage = async (displayMode = mainTextDisplayMode) => {
    setAddingMainText(true);
    try {
      await addMainTextToPageFromHook(displayMode);
    } finally {
      setAddingMainText(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langParam = searchParams.get('plang');

    if (langParam) {
      setPageLanguage(langParam);
    }
  }, [location.search]);

  useSyncLanguageUrl(pageLanguage);

  useEffect(() => {
    setFinishedPreviewPath(pageData?.[`image_preview_${pageLanguage}`] || '');
  }, [pageData, pageLanguage]);

  useEffect(() => {
    let cancelled = false;
    const loadPageData = async () => {
      resetArtwork();
      clearMoveHistory();
      setTextItems([]);
      setPageTextEntries([]);
        setFinishedPreviewPath('');
      setPageLoadComplete(false);
      setSelectedTextItemId(null);
      setSelectedVectorLayoutId('');
      setPrevPage(null);
      setNextPage(null);
      setBookDefaultPresetId(undefined);
      try {
        const response = await getPage(id);
        if (cancelled) return;
        logLayoutDebug('page-loaded', {
          pageId: id,
          canvasSize: { width: response.data.width, height: response.data.height },
          responseTextItems: (response.data.text_items || []).map(describeLayoutItem),
        });
        setPageData(response.data);
        try {
          const textItemsResponse = await getPageTextItems(id);
          if (cancelled) return;
          const normalizedItems = normalizeSavedTextItems(textItemsResponse.data.text_items || []);
          logLayoutDebug('text-items-loaded', {
            pageId: id,
            rawItems: (textItemsResponse.data.text_items || []).map(describeLayoutItem),
            normalizedItems: normalizedItems.map(describeLayoutItem),
          });
          setTextItems(normalizedItems);
        } catch (textItemsError) {
          if (cancelled) return;
          console.error('Failed to load page text items', textItemsError);
          const normalizedItems = normalizeSavedTextItems(
            Array.isArray(response.data.text_items) ? response.data.text_items : [],
          );
          setTextItems(normalizedItems);
        }

        // Fetch book's default preset
        try {
          const bookResponse = await getBook(response.data.book_id);
          if (cancelled) return;
          setBookDefaultPresetId(bookResponse.data.default_preset_id || null);
        } catch {
          if (cancelled) return;
          setBookDefaultPresetId(null);
        }

        // Fetch prev/next pages for thumbnails
        try {
          const prevResponse = await getPreviousPage(id);
          if (cancelled) return;
          setPrevPage(prevResponse.data);
        } catch {
          if (cancelled) return;
          setPrevPage(null);
        }
        try {
          const nextResponse = await getNextPage(id);
          if (cancelled) return;
          setNextPage(nextResponse.data);
        } catch {
          if (cancelled) return;
          setNextPage(null);
        }
        try {
          const textEntriesResponse = await getPageTextEntries(id);
          if (cancelled) return;
          setPageTextEntries(
            normalizePageTextEntries(textEntriesResponse.data.page_text_entries),
          );
        } catch (textEntriesError) {
          if (cancelled) return;
          console.error('Failed to load additional text', textEntriesError);
          setPageTextEntries([]);
        }
        setPageLoadComplete(true);

        // Use full resolution canvas (zoom/pan handled by Stage scaleX/scaleY)
        setCanvasSize({
          width: response.data.width,
          height: response.data.height,
        });

        if (response.data?.image_path) {
          loadImageSafely(`/uploads/${response.data?.image_path}`);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load page data', error);
      }
    };

    if (id) {
      loadPageData();
    }
    return () => { cancelled = true; };
  }, [
    clearMoveHistory,
    id,
    loadImageSafely,
    normalizePageTextEntries,
    normalizeSavedTextItems,

    resetArtwork,
    setBookDefaultPresetId,

    setPageTextEntries,
    setSelectedTextItemId,
    setSelectedVectorLayoutId,
    setTextItems,
  ]);

  const saveRichTextContent = async (htmlContent, langCode) => {
    const dynamicField = `content_html_${langCode}`;
    try {
      const response = await saveRichText(id, dynamicField, htmlContent);
      if (response.status === 200) {
        notify(`Text saved.`);
      }
    } catch (err) {
      console.error('Error saving rich text:', err);
      notify('Content already exists', 'error');
    }
  };

  const {
    exportToPDF,
    handleStageClick,
    renderGridLines,
    resetPreviewImage,
    savePage,
  } = useCanvasOutputController({
    canvasSize,
    describeLayoutItem,
    effectiveStageScale,
    getCurrentTextValues,
    id,
    logLayoutDebug,
    notify,
    pageData,
    pageLanguage,

    saveTextItems,

    setRemovingRenderedPage,
    setPageData,

    setRenderingPreview,
    setSavingPage,
    setSelectedId,
    setSelectedTextItemId,

    stageRef,
    textItemsRef,
  });

  if (!pageData) return <div>Loading...</div>;

  const { width, height } = canvasSize;
  // Page numbers are already stored and displayed as book-page numbers elsewhere
  // (for example in the Pages list). Do not offset this editor label.
  const displayPageNumber = pageData.page_number;
  const pageOrientation = width === height ? 'Square' : width > height ? 'Wide' : 'Tall';
  const currentLanguageTextItems = orderLayoutBoxes(textItems, pageTextEntries);
  const visibleMainTextItems = currentLanguageTextItems.filter(
    (item) => item.role === MAIN_TEXT_ROLE && isTextItemVisibleOnCanvas(item),
  );
  const activeMainTextVisibility = visibleMainTextItems[0]?.layoutConfig?.textVisibility || visibleMainTextItems[0]?.style?.textVisibility || mainTextDisplayMode;
  const selectedStructuredItem = currentLanguageTextItems.find(
    (item) => item.id === selectedTextItemId,
  ) || null;
  const selectedStructuredEntry = selectedStructuredItem?.role === ADDITIONAL_TEXT_ROLE
    ? pageTextEntries.find((entry) => entry.id === selectedStructuredItem.sourceTextEntryId) || null
    : null;
  const removeLayoutBox = async (boxId) => {
    const next = textItemsRef.current.filter((item) => item.id !== boxId);
    const saved = await saveTextItems(next);
    if (saved) { textItemsRef.current = saved; setSelectedTextItemId(null); }
    return Boolean(saved);
  };
  const toggleLayoutBoxVisibility = (boxId) => {
    const next = textItemsRef.current.map((item) => item.id === boxId
      ? { ...item, visibleOnCanvas: item.visibleOnCanvas === false } : item);
    textItemsRef.current = next;
    setTextItems(next);
  };
  const addWordingToLayout = async (entryId) => {
    setActiveWorkflowTab('page-layout');
    return entryId ? insertAdditionalTextBlock(entryId) : addMainTextToPage(activeMainTextVisibility);
  };
  const renamingEntry = pageTextEntries.find((entry) => entry.id === renamingEntryId);
  const reusableTextEntries = [
    { id: 'page-main', label: 'Main text' },
    ...pageTextEntries,
  ].flatMap((entry) => {
    const boxes = currentLanguageTextItems.filter((item) => entry.id === 'page-main'
      ? item.role === MAIN_TEXT_ROLE : item.sourceTextEntryId === entry.id);
    return boxes.some(isTextItemVisibleOnCanvas) ? [] : [{ ...entry, hidden: boxes.length > 0 }];
  });
  return (
    <>
      <Notification />
      {renamingEntry && <RenameTextBoxModal
        key={renamingEntry.id}
        name={renamingEntry.label || 'Text box'}
        saving={savingTextEntries}
        onClose={() => setRenamingEntryId(null)}
        onSave={async (name) => {
          const renamed = await renamePageTextEntry(renamingEntry.id, name);
          if (renamed) setRenamingEntryId(null);
          return renamed;
        }}
      />}
      <AdditionalContentModal
        open={showAdditionalContentModal}
        languages={['en', ...currentLanguages.filter((language) => language !== 'en')]}
        value={additionalContentDraft}
        onChange={setAdditionalContentDraft}
        onClose={() => setShowAdditionalContentModal(false)}
        onSave={async () => {
          const entry = await addPageTextEntry();
          if (entry) await insertAdditionalTextBlock(entry.id, undefined, entry);
        }}
        availableEntries={reusableTextEntries}
        onUseExisting={(entryId) => addWordingToLayout(entryId === 'page-main' ? null : entryId)}
        saving={savingTextEntries}
      />

      <div
        className="min-h-screen bg-slate-100"
      >
        <div className="mx-auto max-w-[1400px] space-y-4 px-6 py-6">
        <EditPageHeader
          displayPageNumber={displayPageNumber}
          pageData={pageData}
          pageLanguage={pageLanguage}
          previousPage={prevPage}
          nextPage={nextPage}
          onPageLanguageChange={setPageLanguage}
          rendering={savingPage || renderingPreview}
          onPreviousPage={async () => {
            try {
              const response = await getPreviousPage(id, pageLanguage);
              navigate(`/edit/${response.data._id}?plang=${pageLanguage}`);
            } catch {
              navigate(`/pages/${pageData.book_id}?plang=${pageLanguage}`);
            }
          }}
          onNextPage={async () => {
            try {
              const response = await getNextPage(id, pageLanguage);
              navigate(`/edit/${response.data._id}?plang=${pageLanguage}`);
            } catch {
              console.error('No next page found.');
            }
          }}
        />

        <RenderedPagePanel
          key={`${id}:${pageLanguage}`}
          path={finishedPreviewPath}
          language={pageLanguage}
          busy={savingPage || renderingPreview || removingRenderedPage}
          onRemove={resetPreviewImage}
        />

        <ZoomToolbar
          stageScale={stageScale}
          showGrid={showGrid}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetZoom={handleResetZoom}
          onShowGridChange={setShowGrid}
        />

        <div className="grid items-start gap-4 xl:grid-cols-[320px_minmax(0,1fr)_320px]">
          <div className="contents">
          <WorkflowSidebar
            editor={{
              bookDefaultPresetId,
              ...pageLayoutTemplates,
              activeWorkflowTab,
              activeMainTextVisibility,
              toggleLayoutBoxVisibility,
              removeLayoutBox,
              addMainTextToPage,
              applyVectorLayout,
              addingMainText,
              deletePageTextEntry,
              designPresets,
              exportToPDF,
              savePage,
              removingRenderedPage,
              saveLayout: () => saveTextItems(textItemsRef.current, {
                notifyMessage: 'Page layout saved.',
                              }),
              savingTextItems,
              savingPage,
              renderingPreview,
              addWhiteCover,
              handleImageUpload,
              handleVectorLayoutSelection,
              imageUploadRef,
              openAdditionalContentModal,
              pageData,
              pageTextEntries,
              renamePageTextEntry: setRenamingEntryId,
              savingTextEntries,
              selectedTextItemId,
              selectedVectorLayoutId,
              setActiveWorkflowTab,
              setSelectedId,
              setSelectedTextItemId,
              textItems,
              vectorLayouts,
            }}
          />
          </div>
          <div className="min-w-0 xl:sticky xl:top-20">
          <PageCanvasPanel
            editor={{
              designPresets,
              bookDefaultPresetId,
              additionalTextRole: ADDITIONAL_TEXT_ROLE,
              canUndoMove,
              canvasSize,
              canvasViewportRef,
              currentLanguageTextItems,
              currentLanguages,
              editorReady,
              editorRenderKey,
              effectiveStageScale,
              handleSelect,
              handleStageClick,
              hiddenDecorationIds,
              height,
              image,
              layerRef,
              pageOrientation,
              recordMove,
              renderingPreview,
              renderGridLines,
              removingRenderedPage,
              savePage,
              savingPage,
              savingTextItems,
              selectedId,
              selectedTextItemId,
              selectedVectorAssets,
              selectedVectorLayout,
              setActiveWorkflowTab,
              setSelectedAdditionalEntryId,
              setSelectedId,
              setSelectedTextItemId,
              setTextNodes,
                        showGrid,
              stageRef,
              textNodes,
              trRef,
              updatePageTextItemPosition,
              undoMove,
              width,
            }}
          />
          </div>
          <SelectionInspector
            editor={{
              bookDefaultPresetId,
              canvasSize,
              changeLayoutBox: (box) => {
                const next = textItemsRef.current.map((item) => item.id === box.id ? box : item);
                textItemsRef.current = next;
                setTextItems(next);
              },
              toggleLayoutBoxVisibility,
              saveLayoutBox: (boxId) => saveTextItem(boxId),
              removeLayoutBox,
              activeWorkflowTab,
              designPresets,
              pageLanguage,
              savingTextItems,
              selectedId,
              selectedStructuredEntry,
              selectedStructuredItem,
              setSelectedId,
              setTextNodes,
              textNodes,
              recordMove,
            }}
          />
        </div>

        <section className={activeWorkflowTab === 'page-layout' ? 'space-y-4' : 'hidden'} aria-label="Page wording and translations">
          <PageTextEditor editor={{
            additionalEntries: pageTextEntries,
            layoutItems: currentLanguageTextItems,
            onAddToLayout: addWordingToLayout,
            onToggleLayoutBox: toggleLayoutBoxVisibility,
            layoutBusy: savingTextItems || addingMainText || savingPage || renderingPreview,
                bookId: pageData.book_id,
                onContentSave: saveRichTextContent,
                onImageGenerated: (image) => { handleRichTextImage(image); setActiveWorkflowTab('page-layout'); },
                onRenameAdditionalEntry: setRenamingEntryId,
                renamingText: savingTextEntries,
                onRemoveAdditionalEntry: deletePageTextEntry,
                onSaveAdditionalEntries: () => savePageTextEntries(pageTextEntries),
                onUpdateAdditionalEntryText: updateAdditionalEntryText,
                pageId: id,
                pageLanguage,
                saveTextChanges,
                savingFields,
                setTextByLang,
                textByLang,
                translateText,
          }} />
        </section>

        </div>
      </div>
    </>
  );
}

export default EditPagePresets;
